import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Download, FileText, Video, BookOpen, Users, Calendar, ExternalLink, Search, ArrowRight } from 'lucide-react';

const Resources = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [resources, setResources] = useState([]);

  const categories = [
    { id: 'all', name: 'Todos los recursos' },
    { id: 'Guías', name: 'Guías y documentos' },
    { id: 'Instrumentos', name: 'Instrumentos de evaluación' },
    { id: 'Plantillas', name: 'Plantillas y formatos' },
    { id: 'Manuales', name: 'Manuales' },
    { id: 'Infografías', name: 'Infografías' },
    { id: 'Estudios', name: 'Estudios de caso' },
    { id: 'Referencias', name: 'Material de referencia' }
  ];

  // Cargar recursos desde JSON
  useEffect(() => {
    fetch('/data/resources.json')
      .then(response => response.json())
      .then(data => setResources(data))
      .catch(error => {
        console.error('Error cargando recursos:', error);
        setResources([]);
      });
  }, []);

  const webinars = [
    {
      title: "Innovaciones en la evaluación digital",
      date: "2025-02-15",
      time: "15:00 GMT-6",
      speaker: "Dr. Elena Martínez",
      description: "Nuevas estrategias para evaluar competencias en entornos virtuales",
      registrationLink: "#"
    },
    {
      title: "Herramientas de engagement estudiantil",
      date: "2025-02-22",
      time: "16:00 GMT-6",
      speaker: "Prof. Carlos Mendoza",
      description: "Técnicas para mantener la motivación en clases virtuales",
      registrationLink: "#"
    },
    {
      title: "Diseño instruccional para educación híbrida",
      date: "2025-03-01",
      time: "14:00 GMT-6",
      speaker: "Dra. Ana Torres",
      description: "Principios y mejores prácticas para modalidades mixtas",
      registrationLink: "#"
    }
  ];

  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getResourceIcon = (type) => {
    switch (type) {
      case 'PDF':
        return <FileText className="h-6 w-6 text-red-500" />;
      case 'Excel':
      case 'XLSX':
        return <FileText className="h-6 w-6 text-green-500" />;
      case 'DOCX':
      case 'DOC':
        return <FileText className="h-6 w-6 text-blue-500" />;
      case 'PNG':
      case 'JPG':
      case 'JPEG':
        return <FileText className="h-6 w-6 text-purple-500" />;
      case 'video':
        return <Video className="h-6 w-6 text-orange-500" />;
      default:
        return <FileText className="h-6 w-6 text-gray-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[400px] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
            alt="Biblioteca y recursos educativos"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50/65 to-white/75"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <BookOpen className="h-20 w-20 text-orange-600 mx-auto mb-6" />
          <h1 className="text-5xl font-bold mb-6 text-gray-900">
            <span className="text-orange-600">Recursos</span> y Materiales
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Accede a guías, tutoriales, webinars y herramientas diseñadas
            para apoyarte en tu proceso de certificación docente
          </p>
        </div>
      </section>      {/* Search and Filter */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar recursos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${selectedCategory === category.id
                    ? 'bg-amber-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-amber-50 hover:text-amber-600'
                    }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Biblioteca de Recursos
            </h2>
            <p className="text-lg text-gray-600">
              Documentos, guías y materiales de apoyo para tu proceso de certificación
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource) => (
              <div key={resource.id} className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex flex-col h-full">
                <div className="p-6 flex-grow flex flex-col">
                  <div className="flex items-center space-x-3 mb-4">
                    {getResourceIcon(resource.type)}
                    <div className="flex-grow">
                      <h3 className="text-lg font-semibold text-gray-800">
                        {resource.title}
                      </h3>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                          {resource.type}
                        </span>
                        <span className="text-xs text-gray-500">
                          {resource.size}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-4 text-sm flex-grow">
                    {resource.description}
                  </p>

                  {/* Botón y fecha siempre al fondo */}
                  <div className="flex items-center justify-between mt-auto">
                    <span className="text-xs text-gray-500">
                      Actualizado: {new Date(resource.lastUpdated).toLocaleDateString()}
                    </span>
                    <a
                      href={resource.downloadUrl}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-2 bg-red-800 text-white text-sm font-medium rounded-md hover:bg-red-900 transition-colors duration-200"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Descargar
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredResources.length === 0 && (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                No se encontraron recursos que coincidan con tu búsqueda.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Upcoming Webinars */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Webinars y Conferencias
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Eventos en vivo con expertos internacionales en docencia digital y metodologías innovadoras
            </p>
            <div className="w-24 h-1 bg-amber-600 mx-auto mt-6"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {webinars.map((webinar, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col h-full">
                <div className="bg-red-800 text-white p-4 min-h-[120px] flex flex-col justify-between">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="h-5 w-5" />
                    <span className="text-sm font-medium">
                      {new Date(webinar.date).toLocaleDateString()} - {webinar.time}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold leading-tight">
                    {webinar.title}
                  </h3>
                </div>

                <div className="p-6 flex-grow flex flex-col">
                  <div className="flex items-center space-x-2 mb-4">
                    <Users className="h-5 w-5 text-gray-400" />
                    <span className="text-gray-600 font-medium">{webinar.speaker}</span>
                  </div>

                  <p className="text-gray-600 mb-6 flex-grow">
                    {webinar.description}
                  </p>

                  <button className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors duration-200 inline-flex items-center justify-center mt-auto">
                    <ExternalLink className="h-5 w-5 mr-2" />
                    Registrarse Gratis
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-2xl border border-gray-200 p-10 relative overflow-hidden">
            {/* Subtle decorative element */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-50 to-transparent rounded-full -translate-y-32 translate-x-32 opacity-60"></div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
              {/* Content */}
              <div>
                <h2 className="text-3xl font-semibold text-gray-900 mb-4 leading-tight">
                  Recursos Especializados
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Accede a nuestra biblioteca completa de recursos académicos y herramientas
                  para el desarrollo profesional en educación digital.
                </p>

                {/* Services list - more professional */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center text-gray-700">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-4 flex-shrink-0"></div>
                    <span className="text-base font-medium">Guías de preparación especializadas</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-4 flex-shrink-0"></div>
                    <span className="text-base font-medium">Webinars y talleres interactivos</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-4 flex-shrink-0"></div>
                    <span className="text-base font-medium">Material de referencia actualizado</span>
                  </div>
                </div>

                {/* CTA Button 
                <a
                  href={
                    /Mobi|Android/i.test(navigator.userAgent)
                      ? "https://wa.me/+523173851203?text=Solicito informacion sobre acceso premium a recursos"
                      : "https://web.whatsapp.com/send?phone=+523173851203&text=Solicito informacion sobre acceso premium a recursos"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-red-800 text-white font-medium rounded-lg hover:bg-red-900 transition-colors duration-200 shadow-sm hover:shadow-md"
                >
                  Solicitar Acceso Premium
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>*/}
              </div>

              {/* Resource Stats */}
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Biblioteca de Recursos
                  </h3>
                  <p className="text-sm text-gray-600">
                    Contenido actualizado mensualmente
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Guías de estudio</span>
                    <span className="text-sm font-semibold text-orange-600">25+ documentos</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Videos instructivos</span>
                    <span className="text-sm font-semibold text-orange-600">40+ horas</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Plantillas y formatos</span>
                    <span className="text-sm font-semibold text-orange-600">15+ herramientas</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Casos de éxito</span>
                    <span className="text-sm font-semibold text-green-600">50+ ejemplos</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Resources;